import {
  Component,
  type ComponentType,
  type ContextType,
  type ErrorInfo,
  type PropsWithChildren,
} from "react";

import * as Sentry from "@sentry/react";

import { AccountContext } from "../providers/AccountProvider";

const chunkLoadRegex =
  /(failed to fetch|error loading) dynamically imported module/i;

const cssLoadRegex = /Unable to preload CSS/i;

const domMutationRegex =
  /Failed to execute '(insertBefore|removeChild|appendChild)' on 'Node'/i;

const extensionStackRegex =
  /chrome-extension|moz-extension|safari-extension|grammarly|translate/i;

const MAX_DOM_MUTATION_RETRIES = 3;

type ErrorComponent = ComponentType<{
  errorMessage: string;
}>;

// this is a legacy react feature; hasn't been brought to hook form yet

export class ErrorBoundary extends Component<
  PropsWithChildren<{
    errorComponent: ErrorComponent;
  }>
> {
  state = {
    hasError: false,
    errorMessage: "",
  };

  private domMutationRetries = 0;

  static contextType = AccountContext;
  declare context: ContextType<typeof AccountContext>;

  static getDerivedStateFromError(error: Error) {
    if (domMutationRegex.test(error?.message)) {
      return { hasError: false, errorMessage: "" };
    }
    return {
      hasError: true,
      errorMessage: error.message,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error", error, errorInfo);
    const isChunkLoadError =
      error?.name === "ChunkLoadError" ||
      chunkLoadRegex.test(error?.message) ||
      cssLoadRegex.test(error?.message);
    const isDomMutationError = domMutationRegex.test(error?.message);

    if (isDomMutationError) {
      this.domMutationRetries++;
      if (this.domMutationRetries >= MAX_DOM_MUTATION_RETRIES) {
        this.setState({ hasError: true, errorMessage: error.message });
      }
    }

    const suspectedSource = isDomMutationError
      ? extensionStackRegex.test(error?.stack ?? "")
        ? "browser_extension"
        : "unknown_dom_manipulation"
      : undefined;

    try {
      Sentry.captureException(error, {
        level: isDomMutationError
          ? "warning"
          : isChunkLoadError
            ? "log"
            : "error",
        tags: {
          product: this.context.product,
          currentAccountId: this.context.currentAccount?.id,
          email: this.context.liveAccounts?.[0]?.email,
          component: "ErrorBoundary",
          ...(suspectedSource && { suspected_source: suspectedSource }),
        },
        extra: {
          errorInfo,
          ...(isDomMutationError && {
            dom_mutation_retry: this.domMutationRetries,
          }),
          accountContext: {
            isLoading: this.context.isLoading,
            liveAccounts: this.context.liveAccounts,
            paperAccounts: this.context.paperAccounts,
          },
        },
      });
      if (isChunkLoadError) {
        window.location.reload();
      }
    } catch (error) {
      console.error("Failed to record error", error);
    }
  }

  render() {
    if (this.state.hasError) {
      const ErrorComponent = this.props.errorComponent;
      return <ErrorComponent errorMessage={this.state.errorMessage} />;
    }

    return this.props.children;
  }
}
